import * as React from "react";
import { useHistory } from "react-router";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { CardContent, Paper, CardActions } from "@mui/material";
import { MdCheckCircle } from "react-icons/md";

import { PatientData } from "../Contexts";
import InfoModule from "../layout/InfoModule";
import * as CONST from "../Constants";
import * as API from "../api";
import Flash from "../layout/Flash";
import Loading from "../../../shared/Loading";

export default function OnboardingPage({ setData, notifications }) {
  const patient = React.useContext(PatientData);
  const history = useHistory();
  const [loading, setLoading] = React.useState(false);
  const [flash, setFlash] = React.useState(null);
  const steps = patient.patient_info_modules.map((i) => i.title);
  const [activeStep, setActiveStep] = React.useState(patient.onboarding_step);
  const [completed, setCompleted] = React.useState({});

  let patientInfoModulesLength = patient.patient_info_modules.length - 1;
  let content = {
    title: patient.patient_info_modules[activeStep] ? patient.patient_info_modules[activeStep].title : patient.patient_info_modules[patientInfoModulesLength].title,
    body:  patient.patient_info_modules[activeStep] ? patient.patient_info_modules[activeStep].body : patient.patient_info_modules[patientInfoModulesLength].body,
    idx: activeStep
  };


  const hasElectedPcp =
    (patient.pcp && patient.pcp.name) || (patient.ooa_pcp && patient.ooa_pcp.first_name)
      ? true
      : false;

  const hasElectedPcpPractice = patient.elected_pcp_practice != undefined;

  React.useEffect(() => {
    if (activeStep !== 0) {
      const stepsArr = Array.from({ length: activeStep }, (v, k) => k);
      stepsArr.map((n) => {
        const newCompleted = completed;
        newCompleted[n] = true;
        setCompleted(newCompleted);
      });
    }
  }, []);

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    setLoading(true);
    let step = activeStep + 1;
    API.postToPatientSettings({
      onboarding_step: step,
    })
      .then((res) => {
        setData(step);
        const newCompleted = completed;
        newCompleted[activeStep] = true;
        setCompleted(newCompleted);
        handleNext();
        setLoading(false);
        step == steps.length &&
          setFlash({
            success: CONST.FLASH.ONBOARDING_COMPLETED,
          });
      })
      .catch((err) => {
        setFlash({ ...err });
        setLoading(false);
      });
  };

  return (
    <>
      {flash && <Flash flash={flash} handleClose={() => setFlash(null)} showButton={false} />}
      <div className="patient-info">
        <Box
          className="stepper"
          sx={{
            mb: 4,
            display: {
              xs: "none",
              md: "flex",
            },
          }}
        >
          <Stepper nonLinear activeStep={activeStep} sx={{ py: 3, px: 2, m: 1 }}>
            {steps.map((label, index) => (
              <Step key={label} completed={completed[index]} sx={{ mr: 1 }}>
                <StepButton className="step-button" color="inherit" onClick={handleStep(index)}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>

        {loading ? (
          <Loading message="loading" />
        ) : (
          <>
            {activeStep === steps.length ? (
              <Box p={2}>
                <Typography
                  variant="h3"
                  fontSize={"3.1rem"}
                  color="#1a237e"
                  fontWeight="400"
                  sx={{ my: 2 }}
                >
                  <MdCheckCircle className="mb-3 mr3" />
                  {CONST.STEPS_COMPLETED}
                </Typography>
                <Typography fontSize="21px" color="#1a237e" fontWeight="300">
                  {CONST.ONBOARDING_COMPLETED}
                </Typography>
                <Box py={1} color="#1a237e" fontSize={"2rem"} mt={3}>
                  {hasElectedPcpPractice && !hasElectedPcp && <>
                     <b>{ CONST.needPcpElectionFromPcpPractice(patient.elected_pcp_practice) }</b>
                     <br />
                  </>}
                  {!hasElectedPcpPractice && !hasElectedPcp && !patient.out_of_area && <> {CONST.IN_AREA_PCP_ELECTION}</>}
                  {!hasElectedPcpPractice && !hasElectedPcp && patient.out_of_area && (
                    <> {CONST.ONBOARDING_OOA_ELECT_PCP_PROMPT}</>
                  )}
                  {!!notifications.length > 0 && hasElectedPcp && (
                    <>{notifications.length + CONST.DASHBOARD.NOTIFICATIONS_1}</>
                  )}

                  <CardActions sx={{ px: 0, pt: 1 }}>
                    <button
                      className="color"
                      onClick={(e) => {
                        let path = !hasElectedPcp
                          ? !patient.out_of_area
                            ? "/patient_portal/pcp_election"
                            : "/patient_portal/dashboard"
                          : !!notifications.length > 0
                          ? "/patient_portal/notifications/" + notifications[0].id
                          : "/patient_portal/dashboard";

                        if (hasElectedPcpPractice && !hasElectedPcp && !patient.out_of_area) {
                          path = `/patient_portal/practice/${ patient.elected_pcp_practice.id }`;
                        }
                        history.push(path);
                      }}
                    >
                      {!hasElectedPcp
                        ? !patient.out_of_area
                          ? CONST.GO_TO_PCP_ELECTION
                          : CONST.GO_TO_DASHBOARD
                        : !!notifications.length > 0
                        ? CONST.GO_TO_NOTIFICATIONS
                        : CONST.GO_TO_DASHBOARD}
                    </button>
                  </CardActions>
                </Box>
              </Box>
            ) : (
              <InfoModule {...content} />
            )}
          </>
        )}

        <Paper
          className="notifications-stepper"
          elevation={0}
          sx={{
            pt: 1,
            position: "fixed",
            bottom: 0,
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "-webkit-inline-flex",
              pl: { sm: 7 },
            }}
          >
            <Box
              color="#1a237e"
              fontWeight="600"
              mt={1}
              pl={1}
              mr={2.5}
              sx={{ display: "inline-block" }}
            >
              Step {activeStep + 1}
            </Box>

            <Box sx={{ flex: "1 1 auto" }}>
              <Button
                color="inherit"
                disabled={activeStep === 0}
                onClick={(e) => handleBack()}
                sx={{ mr: 1 }}
              >
                {CONST.BACK}
              </Button>

              {activeStep !== steps.length && (
                <>
                  {completed[activeStep] ? (
                    <Button onClick={(e) => handleNext()}>{CONST.NEXT}</Button>
                  ) : (
                    <Button onClick={(e) => handleComplete()}>
                      {completedSteps() === totalSteps() - 1 ? CONST.FINISH : CONST.NEXT}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </div>
    </>
  );
}
